/* eslint-disable @nx/workspace/a11y/require-aria-live-for-toast */
/* eslint-disable @nx/workspace/a11y/require-focus-lock */
/* eslint-disable @nx/workspace/a11y/require-dialog-role */
/* eslint-disable @nx/workspace/a11y/forbid-missing-initial-focus */
/* eslint-disable formatjs/no-literal-string-in-jsx */
'use client';
import React, { useRef } from 'react';
import {
  textGray,
  text3,
  textGray900,
  text4,
} from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import {
  FormattedDate,
  FormattedMessage,
  IntlShape,
  useIntl,
} from 'react-intl';
import ModalHeader from '../ModalHeader';
import Modal, { ModalProps } from '../Modal';
import { Citation } from '@osmosis/shared/types/learn';
import { Contributor } from '@osmosis/shared/types/learn';
import { Updates } from '@osmosis/shared/types/learndb/learnData';
import Snackbar from '../Snackbar';
import useModal from '../../hooks/useModal';
import { usePagesContext } from '../../context/PagesProvider';
import { bgBase, bg1, bg2 } from '@osmosis/tailwind-ui/styles/background';
import { getDisplayDate, safeFormat, DisplayDateType } from './dateUtils';

const CONTRIBUTOR_ORDER = [
  'content reviewer',
  'author',
  'illustrator',
  'voiceover',
  'editor',
];

export const videoInformation = 'video-info-header';
export const videoLastReviewed = 'video-info-last-reviewed';
export const videoLastUpdated = 'video-info-last-updated';
export const videoPageCreated = 'video-info-page-created';
export const videoContributors = 'video-info-contributors';
export const videoCitation = 'video-info-citation';
export const videoReport = 'video-info-report';
export const videoCitationBox = 'video-info-citation-box';
export const videoInfoBox = 'video-info-box';

/** Per-date-type test id for the modal date cell. */
const DATE_TEST_IDS: Record<DisplayDateType, string> = {
  lastReviewed: videoLastReviewed,
  lastUpdated: videoLastUpdated,
  pageCreated: videoPageCreated,
};

/**
 * Resolves the (translated) aria-label for the modal date cell. Reuses the
 * same message ids as the visible label so the strings aren't duplicated.
 * `intl.formatMessage` requires a static descriptor, so the id/defaultMessage
 * cannot be looked up dynamically.
 */
function getDateAriaLabel(
  intl: IntlShape,
  type: DisplayDateType,
  date: string,
): string {
  const values = { date: safeFormat(date) };
  if (type === 'lastReviewed') {
    return intl.formatMessage(
      {
        id: 'learn.video.last_reviewed',
        defaultMessage: 'Last reviewed: {date}',
      },
      values,
    );
  }
  if (type === 'lastUpdated') {
    return intl.formatMessage(
      {
        id: 'learn.video.last_updated',
        defaultMessage: 'Last updated: {date}',
      },
      values,
    );
  }
  return intl.formatMessage(
    {
      id: 'learn.video.page_created',
      defaultMessage: 'Page created: {date}',
    },
    values,
  );
}

/**
 * Renders the visible date label for the given date type. Uses literal
 * `FormattedMessage` ids/defaults (required by the i18n lint rule).
 */
function DateLabelMessage({
  type,
  date,
}: {
  type: DisplayDateType;
  date: string;
}): JSX.Element {
  const formattedDate = (
    <FormattedDate
      key="date"
      value={new Date(date)}
      year="numeric"
      month="long"
      day="2-digit"
    />
  );

  if (type === 'lastReviewed') {
    return (
      <FormattedMessage
        id="learn.video.last_reviewed"
        defaultMessage="Last reviewed: {date}"
        values={{ date: formattedDate }}
      />
    );
  }
  if (type === 'lastUpdated') {
    return (
      <FormattedMessage
        id="learn.video.last_updated"
        defaultMessage="Last updated: {date}"
        values={{ date: formattedDate }}
      />
    );
  }
  return (
    <FormattedMessage
      id="learn.video.page_created"
      defaultMessage="Page created: {date}"
      values={{ date: formattedDate }}
    />
  );
}

function listContributors(contributors: Contributor[]): JSX.Element {
  return (
    <>
      {contributors.map((contributor, index) => (
        <span key={`${contributor.name}-${contributor.id}`}>
          {contributor.name}
          {index < contributors.length - 1 ? ', ' : ''}
        </span>
      ))}
    </>
  );
}

type Props = ModalProps & {
  citations: Citation;
  modified: string;
  toggleFeedbackModal: () => void;
  toggle: () => void;
  title?: string;
  updates?: Updates[];
  learnPageCreationDate?: string;
  lastUpdated?: string; // Already includes fallback logic from next.ts (manual → learn_updates → video upload)
  lastReviewed?: string;
  isVideoPage?: boolean;
  loggedIn: boolean;
  isFeedbackModalVisible: boolean;
  showViews?: boolean;
  views: number;
  isAnswerPage?: boolean;
};

/**
 * .
 */
export default function VideoInformation(props: Props): JSX.Element {
  const {
    toggle: toggleVideoInfoModal,
    toggleFeedbackModal,
    citations,
    learnPageCreationDate,
    lastUpdated, // Includes fallback logic from next.ts: manual → learn_updates → video upload
    lastReviewed,
    isVideoPage = true,
    title,
    loggedIn,
    isFeedbackModalVisible,
    showViews,
    views,
  } = props;

  const intl = useIntl();

  const displayDate = getDisplayDate({
    lastReviewed,
    lastUpdated,
    pageCreated: learnPageCreationDate,
  });

  const { hide, isVisible, show } = useModal();
  const { asPath } = usePagesContext();

  const origin =
    typeof window !== 'undefined' && window.location.origin
      ? window.location.origin
      : '';

  const URL = `${origin}${asPath}`;

  // Citation helper: display today's date in the citation format used elsewhere
  const getCitationDate = (): string => {
    const d = new Date();
    return `${d.getFullYear()}, ${d.getMonth() + 1}/${d.getDate()}`;
  };

  const roleTitle = {
    author: intl.formatMessage({
      id: 'video-information.author',
      defaultMessage: 'Author',
    }),
    editor: intl.formatMessage({
      id: 'video-information.editor',
      defaultMessage: 'Editor',
    }),
    illustrator: intl.formatMessage({
      id: 'video-information.illustrator',
      defaultMessage: 'Illustrator',
    }),
    reviewer: intl.formatMessage({
      id: 'video-information.сontent-reviewer',
      defaultMessage: 'Content Reviewer',
    }),
    voiceover: intl.formatMessage({
      id: 'video-information.voiceover',
      defaultMessage: 'Voiceover',
    }),
    artist: intl.formatMessage({
      id: 'video-information.artist',
      defaultMessage: 'Artist',
    }),
  };

  const minViewsToShow = 1000;
  const modalCloseAriaLabel = 'Close Video Information';
  const modalReportErrorAriaLabel = 'Report error';
  const modalCopyToClipboardAriaLabel = intl.formatMessage({
    id: 'learn.copy_citation_to_clipboard',
    defaultMessage: 'Copy citation to clipboard',
  });

  // Check if length of any contributor roles is greater than 0
  const hasAnyContributors = Object.values(citations).some(
    (contributors) => contributors.length > 0,
  );

  // DOM reference for clipboard text
  const containerRef = useRef<HTMLInputElement>(null);

  const copySuccess = (): void => {
    show();
  };

  // TODO: Copy error message
  /**
   * .
   */
  function copyError(): void {
    /* */
  }

  /**
   * .
   */
  function handleCopyToClipboard(): void {
    const clipboardText = containerRef?.current?.innerText || '';

    if (navigator?.clipboard) {
      navigator.clipboard
        .writeText(clipboardText)
        .then(copySuccess)
        .catch(copyError);
    }
  }

  /**
   * .
   */
  function reportErrorClickHandler(): void {
    toggleFeedbackModal();
  }

  const modalHeaderText = isVideoPage
    ? intl.formatMessage({
        id: 'video-information.modal-header-text',
        defaultMessage: 'Video information',
      })
    : intl.formatMessage({
        id: 'video-information.modal-header-text-notes',
        defaultMessage: 'Notes information',
      });

  const modalCopiedText = intl.formatMessage({
    id: 'video-information.modal-copied-text',
    defaultMessage: ' Citation copied to clipboard!',
  });

  const modalClassName = [bgBase, (props && (props as any).className) || '']
    .join(' ')
    .trim();

  const h4Style = ['mb-1', text4].join(' ');

  return (
    <Modal
      {...props}
      className={modalClassName}
      isReturnFocus={true}
      disableEscapeClose={isFeedbackModalVisible}
    >
      <ModalHeader
        text={modalHeaderText}
        textStyles={textGray}
        onClose={toggleVideoInfoModal}
        ariaLabel={modalCloseAriaLabel}
        data-testid={videoInformation}
      />

      <Box
        data-testid={videoInfoBox}
        className={`max-w-[500px] h-[60vh] overflow-scroll px-8 py-4`}
      >
        {/* Render contributors and date items in a shared 2-column grid so the
              grid unfolds naturally (2 items per row). Keep visual parity with
              the existing contributor boxes by using the same cell width. */}
        {(hasAnyContributors || displayDate) && (
          <Box className={`mb-4`}>
            <Flex
              style={{ rowGap: 12 }}
              className={`flex-wrap items-start justify-between`}
            >
              {displayDate && (
                <Box
                  data-testid={DATE_TEST_IDS[displayDate.type]}
                  className={`mb-4 w-[48%]`}
                  aria-label={getDateAriaLabel(
                    intl,
                    displayDate.type,
                    displayDate.date,
                  )}
                >
                  <P className={`font-bold ${text4}`}>
                    <DateLabelMessage
                      type={displayDate.type}
                      date={displayDate.date}
                    />
                  </P>
                </Box>
              )}
              {Object.entries(citations)
                .sort(
                  ([a], [b]) =>
                    CONTRIBUTOR_ORDER.indexOf(a.toLowerCase()) -
                    CONTRIBUTOR_ORDER.indexOf(b.toLowerCase()),
                )
                .map((contributer) => {
                  const [key, value]: [string, Contributor[]] = contributer;

                  if (value.length > 0) {
                    return (
                      <Box
                        key={key}
                        data-testid={videoContributors}
                        className={`mb-4 w-[48%]`}
                        aria-label={intl.formatMessage(
                          {
                            id: 'video-information.contributors-label',
                            defaultMessage: 'Contributors: {role}',
                          },
                          { role: roleTitle[key as keyof typeof roleTitle] },
                        )}
                      >
                        <H4 className={h4Style}>
                          {roleTitle[key as keyof typeof roleTitle]}
                        </H4>
                        <P>{listContributors(value)}</P>
                      </Box>
                    );
                  }

                  return null;
                })}
              {showViews && views >= minViewsToShow && (
                <Box
                  className={`mb-4 w-[48%]`}
                  data-testid="video-information.views-count"
                  aria-label={intl.formatMessage(
                    {
                      id: 'video-information.views-count-label',
                      defaultMessage: 'Views: {count}',
                    },
                    { count: views },
                  )}
                >
                  <H4 className={h4Style}>
                    <FormattedMessage
                      id="learn.video.views.count"
                      defaultMessage="Views"
                    />
                  </H4>
                  <P>{views}</P>
                </Box>
              )}{' '}
            </Flex>
          </Box>
        )}

        <Box data-testid={videoCitation} className={`mb-4`}>
          <H4 className={h4Style}>
            <FormattedMessage
              id="learn.video.citation"
              defaultMessage="Citation"
            />
          </H4>
          <Flex
            ref={containerRef}
            data-testid={videoCitationBox}
            className={`flex-nowrap overflow-hidden rounded-[4px]`}
          >
            <P
              className={`basis-[85%] overflow-hidden text-ellipsis ${bg1} p-4`}
            >
              <FormattedMessage id="name.osmosis" defaultMessage="Osmosis" />
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              {`: ${title}. (${getCitationDate()}).`}
              <br />
              <FormattedMessage
                id="learn.video.retrieved_from"
                defaultMessage="Retrieved from ({url})."
                values={{
                  url: URL,
                }}
              />
            </P>
            <Button
              data-testid="learn.video.section.copyToClipboard"
              onClick={handleCopyToClipboard}
              aria-label={modalCopyToClipboardAriaLabel}
              className={`basis-[15%] cursor-pointer items-center justify-center ${bg2} ${text3} hover:bg-gray-500 hover:text-gray-600`}
            >
              <Icon icon={faCopy} size="xl" />
            </Button>
          </Flex>
        </Box>

        {loggedIn && (
          <Box data-testid={videoReport}>
            <H4 className={['mb-2.5', textGray900, 'opacity-75'].join(' ')}>
              <FormattedMessage
                id="learn.video.report"
                defaultMessage="Report"
              />
            </H4>
            <P>
              <FormattedMessage
                id="learn.video.spot_issue"
                defaultMessage="Did you spot an issue? Let us know so we can fix it!"
              />
            </P>
            <Button
              data-testid="learn.video.button.report_error"
              variant="outline"
              style={{
                color: 'gray40',
              }}
              onClick={reportErrorClickHandler}
              aria-label={modalReportErrorAriaLabel}
              className={`mt-2.5 rounded-[30px]`}
            >
              <Icon icon={faCircleInfo} style={{ marginRight: '10px' }} />

              <FormattedMessage
                id="learn.video.report_error"
                defaultMessage="Report error"
              />
            </Button>
          </Box>
        )}
      </Box>
      <Snackbar
        isVisible={isVisible}
        hide={hide}
        message={modalCopiedText}
        variant="gray80"
        autoHide={2000}
        motionStyle={{
          top: 20,
          left: '50%',
          translateX: '-50%',
        }}
        animateProps={{ opacity: 0.9 }}
      />
    </Modal>
  );
}
